import { useEffect, useMemo, useState } from "react";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "./ui/command";
import { Checkbox } from "./ui/checkbox";
import { FilePdf, Download, FloppyDisk, ArrowCounterClockwise, UserCircle, CaretDown, Check } from "@phosphor-icons/react";

const DEFAULTS = {
  member_id: "",
  mode: "month",              // 'month' | 'range' | 'all'
  month: new Date().toISOString().slice(0, 7),
  from_date: "",
  to_date: "",
  status: "all",              // 'all' | 'todo' | 'in_progress' | 'complete'
  include_summary: true,
  title: "",
};

const presetKey = (uid) => `dhp_member_report_preset_${uid || "anon"}`;

function readPreset(uid) {
  try {
    const raw = localStorage.getItem(presetKey(uid));
    if (!raw) return null;
    const p = JSON.parse(raw);
    return {
      ...DEFAULTS,
      ...p,
      mode: ["month", "range", "all"].includes(p.mode) ? p.mode : DEFAULTS.mode,
      status: ["all", "todo", "in_progress", "complete"].includes(p.status) ? p.status : DEFAULTS.status,
      include_summary: typeof p.include_summary === "boolean" ? p.include_summary : true,
    };
  } catch { return null; }
}
function writePreset(uid, p) { try { localStorage.setItem(presetKey(uid), JSON.stringify(p)); } catch { /* ignore */ } }
function clearPreset(uid) { try { localStorage.removeItem(presetKey(uid)); } catch { /* ignore */ } }

export default function MemberWorkReportDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const uid = user?.id;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const initial = () => readPreset(uid) || DEFAULTS;
  const [memberId, setMemberId] = useState(() => initial().member_id);
  const [mode, setMode] = useState(() => initial().mode);
  const [month, setMonth] = useState(() => initial().month);
  const [fromDate, setFromDate] = useState(() => initial().from_date);
  const [toDate, setToDate] = useState(() => initial().to_date);
  const [status, setStatus] = useState(() => initial().status);
  const [includeSummary, setIncludeSummary] = useState(() => initial().include_summary);
  const [title, setTitle] = useState(() => initial().title);
  const [hasPreset, setHasPreset] = useState(() => !!readPreset(uid));
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const r = await http.get("/users");
        // Only staff (not clients) — admin, manager, member
        const staff = r.data
          .filter((u) => ["admin", "manager", "member"].includes(u.role))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setMembers(staff);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
    const p = readPreset(uid);
    setHasPreset(!!p);
    if (p) {
      setMemberId(p.member_id);
      setMode(p.mode); setMonth(p.month); setFromDate(p.from_date); setToDate(p.to_date);
      setStatus(p.status); setIncludeSummary(p.include_summary); setTitle(p.title);
    } else {
      setMemberId(DEFAULTS.member_id); setMode(DEFAULTS.mode); setMonth(DEFAULTS.month);
      setFromDate(DEFAULTS.from_date); setToDate(DEFAULTS.to_date);
      setStatus(DEFAULTS.status); setIncludeSummary(DEFAULTS.include_summary); setTitle(DEFAULTS.title);
    }
  }, [open, uid]);

  const selectedMember = useMemo(
    () => members.find((m) => m.id === memberId),
    [members, memberId],
  );

  const currentConfig = () => ({
    member_id: memberId, mode, month, from_date: fromDate, to_date: toDate,
    status, include_summary: includeSummary, title,
  });

  const buildPayload = () => {
    const base = {
      member_id: memberId,
      status: status === "all" ? null : status,
      include_summary: includeSummary,
      title: title.trim() || null,
    };
    if (mode === "month") base.month = month;
    else if (mode === "range") {
      base.from_date = fromDate || null;
      base.to_date = toDate || null;
    }
    return base;
  };

  const handleExport = async () => {
    if (!memberId) { toast.error("Pick a team member first."); return; }
    if (mode === "month" && !/^\d{4}-\d{2}$/.test(month || "")) {
      toast.error("Pick a valid month (YYYY-MM)."); return;
    }
    if (mode === "range" && !fromDate && !toDate) {
      toast.error("Pick a From or To date, or switch to 'All time'."); return;
    }
    setDownloading(true);
    try {
      const res = await http.post("/tasks/report/member-pdf", buildPayload(), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (selectedMember?.name || "member").replace(/[^a-zA-Z0-9]/g, "-");
      a.download = `Member-Work-${safe}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      writePreset(uid, currentConfig());
      setHasPreset(true);
      toast.success("Member work report downloaded. Saved as your default.");
      onOpenChange?.(false);
    } catch (e) {
      let msg = formatApiError(e);
      try {
        if (e?.response?.data instanceof Blob) {
          const t = await e.response.data.text();
          const parsed = JSON.parse(t);
          if (parsed?.detail) msg = typeof parsed.detail === "string" ? parsed.detail : msg;
        }
      } catch { /* keep */ }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const handleSavePreset = () => {
    writePreset(uid, currentConfig());
    setHasPreset(true);
    toast.success("Saved as your default member-report preset.");
  };
  const handleResetPreset = () => {
    clearPreset(uid);
    setHasPreset(false);
    setMemberId(DEFAULTS.member_id); setMode(DEFAULTS.mode); setMonth(DEFAULTS.month);
    setFromDate(DEFAULTS.from_date); setToDate(DEFAULTS.to_date);
    setStatus(DEFAULTS.status); setIncludeSummary(DEFAULTS.include_summary); setTitle(DEFAULTS.title);
    toast.message("Preset cleared — showing defaults.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="member-report-dialog"
        className="max-w-2xl w-[96vw] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCircle size={22} weight="duotone" className="text-amber-700" />
            Member Work Report
          </DialogTitle>
        </DialogHeader>

        {hasPreset && (
          <div
            data-testid="member-report-preset-banner"
            className="mt-2 flex items-center justify-between gap-2 border border-amber-200 bg-amber-50 px-3 py-2 text-xs"
          >
            <span className="flex items-center gap-2 text-amber-900">
              <FloppyDisk size={14} weight="duotone" />
              Using your saved preset. Changes auto-save when you export.
            </span>
            <button
              type="button"
              data-testid="member-report-reset-preset"
              onClick={handleResetPreset}
              className="inline-flex items-center gap-1 text-amber-800 hover:underline"
            >
              <ArrowCounterClockwise size={12} weight="bold" />
              Reset to defaults
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 mt-2">
          {/* Member picker — searchable combobox (by name or role) */}
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">
              Team Member
            </label>
            <Popover open={memberPickerOpen} onOpenChange={setMemberPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="member-report-member"
                  disabled={loading}
                  className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className={selectedMember ? "text-foreground" : "text-muted-foreground"}>
                    {selectedMember
                      ? <>{selectedMember.name} <span className="text-zinc-400">· {selectedMember.role}</span></>
                      : (loading ? "Loading staff…" : "Search by name or role…")}
                  </span>
                  <CaretDown size={14} className="opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command
                  filter={(value, search) => {
                    // `value` is the lowercased concatenation we set via value={}
                    return value.includes(search.toLowerCase()) ? 1 : 0;
                  }}
                >
                  <CommandInput
                    data-testid="member-report-member-search"
                    placeholder="Type a name or role…"
                  />
                  <CommandList>
                    <CommandEmpty>No matching staff.</CommandEmpty>
                    <CommandGroup>
                      {members.map((m) => (
                        <CommandItem
                          key={m.id}
                          data-testid={`member-opt-${m.id}`}
                          value={`${m.name} ${m.role} ${m.designation || ""}`.toLowerCase()}
                          onSelect={() => {
                            setMemberId(m.id);
                            setMemberPickerOpen(false);
                          }}
                        >
                          <Check
                            size={14}
                            className={`mr-2 ${memberId === m.id ? "opacity-100 text-emerald-700" : "opacity-0"}`}
                          />
                          <span className="flex-1">{m.name}</span>
                          <span className="text-xs text-zinc-400 capitalize">{m.role}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Period selector */}
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">
              Period
            </label>
            <div className="inline-flex rounded-none border-2 border-zinc-900 overflow-hidden" role="tablist">
              {[
                { k: "month", label: "By Month" },
                { k: "range", label: "Date Range" },
                { k: "all",   label: "All Time" },
              ].map((opt) => {
                const active = mode === opt.k;
                return (
                  <button
                    key={opt.k}
                    role="tab"
                    aria-selected={active}
                    data-testid={`member-report-mode-${opt.k}`}
                    onClick={() => setMode(opt.k)}
                    className={
                      "font-mono uppercase tracking-widest text-[10px] px-3 py-2 border-r-2 border-zinc-900 last:border-r-0 transition-colors " +
                      (active ? "bg-zinc-900 text-white" : "bg-white text-zinc-900 hover:bg-zinc-100")
                    }
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
              {mode === "month" && (
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-xs text-zinc-500 mb-1 block">Month</label>
                  <Input
                    type="month"
                    data-testid="member-report-month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  />
                </div>
              )}
              {mode === "range" && (
                <>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">From</label>
                    <Input
                      type="date"
                      data-testid="member-report-from"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">To</label>
                    <Input
                      type="date"
                      data-testid="member-report-to"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                    />
                  </div>
                </>
              )}
              {mode === "all" && (
                <div className="col-span-2 text-xs text-zinc-500 italic self-end">
                  No date filter — every task ever assigned to this member.
                </div>
              )}
            </div>
          </div>

          {/* Status + title */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="member-report-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="complete">Complete only</SelectItem>
                  <SelectItem value="in_progress">In Progress only</SelectItem>
                  <SelectItem value="todo">To Do only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold tracking-wider uppercase text-zinc-500 mb-1 block">Report Title</label>
              <Input
                data-testid="member-report-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={selectedMember ? `Work Report — ${selectedMember.name}` : "Auto-generated from member name"}
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              data-testid="member-report-summary"
              checked={includeSummary}
              onCheckedChange={(v) => setIncludeSummary(!!v)}
            />
            Include summary chips (Total / Complete / In Progress / To Do / Milestones)
          </label>
        </div>

        <DialogFooter className="mt-5 gap-2 sm:justify-between">
          <Button
            variant="outline"
            onClick={handleSavePreset}
            data-testid="member-report-save-preset"
            className="gap-2"
          >
            <FloppyDisk size={14} weight="bold" />
            Save as my default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="member-report-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={downloading || !memberId}
              data-testid="member-report-submit"
              className="bg-amber-700 hover:bg-amber-800 text-white gap-2"
            >
              <Download size={16} weight="bold" />
              {downloading ? "Preparing PDF…" : "Export PDF"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
